<template>
  <BasicTemplate>
    <v-card>
      <v-card-title class="d-flex align-center">
        <h3 class="d-flex align-center mb-0" data-testid="analytics-configs-page-title">
          <v-icon class="mr-2">mdi-chart-timeline-variant</v-icon>
          Analytics configs
        </h3>
        <v-spacer />
        <v-btn
          class="mr-2"
          color="info"
          :loading="loading"
          data-testid="analytics-configs-refresh"
          @click="load"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn
          color="success"
          data-testid="analytics-configs-create"
          @click="$router.push('/analytics-configs/new')"
        >
          Create
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row dense class="mb-4" align="center">
          <v-col cols="12" sm="8" md="6">
            <v-text-field
              v-model="search"
              label="Search"
              placeholder="Id, key, description or domain"
              prepend-icon="mdi-magnify"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="typeFilter"
              :items="typeFilterItems"
              label="Type"
              clearable
              hide-details
            />
          </v-col>
        </v-row>

        <div class="mb-2">
          <v-chip color="info" small>
            Showing {{ filteredItems.length }} of {{ items.length }} configs
          </v-chip>
        </div>

        <v-data-table
          :loading="loading"
          :headers="headers"
          :items="filteredItems"
          item-key="id"
          show-expand
          :expanded.sync="expanded"
          sort-by="id"
        >
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <div class="d-flex align-center">
              <v-btn
                icon
                small
                :aria-expanded="isExpanded ? 'true' : 'false'"
                :data-testid="`analytics-configs-expand-${item.id}`"
                @click.stop="expand(!isExpanded)"
              >
                <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
              <span class="caption text--secondary text-no-wrap">{{ domainsSummary(item) }}</span>
            </div>
          </template>

          <template v-slot:expanded-item="{ headers: tableHeaders, item }">
            <td :colspan="tableHeaders.length" class="pa-0">
              <div class="analytics-domains-expanded px-6 py-4 grey lighten-5">
                <div class="text-caption text--secondary mb-2">Domain samplings</div>
                <ul v-if="domainList(item).length" class="mb-0">
                  <li
                    v-for="(row, i) in domainList(item)"
                    :key="'d-' + item.id + '-' + (row.id || i)"
                    class="py-1"
                  >
                    {{ row.domain }}
                    <span class="text--secondary"> — {{ row.sampling }}%</span>
                  </li>
                </ul>
                <span v-else class="text--secondary font-italic">No domain overrides</span>
              </div>
            </td>
          </template>

          <template v-slot:item.type="{ item }">
            {{ typeLabel(item.type) }}
          </template>
          <template v-slot:item.sampling="{ item }">
            {{ item.sampling }}%
          </template>
          <template v-slot:item.key="{ item }">
            <span class="text-truncate d-inline-block" style="max-width: 280px" :title="item.key">
              {{ item.key }}
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="analytics-configs-actions">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    :data-testid="`analytics-configs-edit-${item.id}`"
                    @click="openEdit(item)"
                  >
                    mdi-pencil
                  </v-icon>
                </template>
                <span>Edit</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="secondary"
                    tag="a"
                    :href="changeLogHref(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                    :data-testid="`analytics-configs-history-${item.id}`"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-history
                  </v-icon>
                </template>
                <span>View change history</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="warning"
                    v-bind="attrs"
                    v-on="on"
                    :data-testid="`analytics-configs-delete-${item.id}`"
                    @click="deleteItem(item)"
                  >
                    mdi-delete
                  </v-icon>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';
import {
  ANALYTICS_CONFIG_TYPES,
  analyticsConfigTypeLabel,
  apiErrorMessage,
} from '../../constants/analyticsConfigTypes';

export default {
  name: 'AnalyticsConfigsList',
  components: { BasicTemplate },
  data() {
    return {
      loading: false,
      search: '',
      typeFilter: null,
      expanded: [],
      items: [],
      headers: [
        { text: '', value: 'data-table-expand', sortable: false, width: '140px' },
        { text: 'Id', value: 'id' },
        { text: 'Type', value: 'type' },
        { text: 'Key', value: 'key' },
        { text: 'Description', value: 'description' },
        { text: 'Sampling %', value: 'sampling' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px', align: 'center' },
      ],
    };
  },
  computed: {
    typeFilterItems() {
      return ANALYTICS_CONFIG_TYPES;
    },
    filteredItems() {
      let list = this.items;
      if (this.typeFilter) {
        list = list.filter((item) => item.type === this.typeFilter);
      }
      const query = (this.search || '').trim().toLowerCase();
      if (!query) return list;
      return list.filter((item) => this.itemMatchesSearch(item, query));
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    typeLabel: analyticsConfigTypeLabel,
    domainList(item) {
      return Array.isArray(item?.domainSamplings) ? item.domainSamplings : [];
    },
    domainsSummary(item) {
      const n = this.domainList(item).length;
      if (n === 0) return 'No overrides';
      return n === 1 ? '1 domain' : `${n} domains`;
    },
    itemMatchesSearch(item, query) {
      const fields = [
        item.id,
        item.type,
        this.typeLabel(item.type),
        item.key,
        item.description,
        item.sampling,
      ];
      if (fields.some((v) => v != null && String(v).toLowerCase().includes(query))) {
        return true;
      }
      return this.domainList(item).some(
        (row) => row?.domain && String(row.domain).toLowerCase().includes(query),
      );
    },
    async load() {
      this.loading = true;
      try {
        const list = await this.$gameApi.get('/analytics-configs');
        this.items = Array.isArray(list) ? list : [];
        this.expanded = [];
      } catch (e) {
        this.items = [];
        this.$swal.fire({
          icon: 'error',
          title: apiErrorMessage(e, 'Failed to load analytics configs'),
          timer: 2500,
          showConfirmButton: false,
        });
      } finally {
        this.loading = false;
      }
    },
    changeLogLink(item) {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'analytics_config',
          entityId: String(item.id),
        },
      };
    },
    changeLogHref(item) {
      return this.$router.resolve(this.changeLogLink(item)).href;
    },
    openEdit(item) {
      this.$router.push(`/analytics-configs/${item.id}`);
    },
    async deleteItem(item) {
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: `Delete analytics config #${item.id}?`,
        text: `${analyticsConfigTypeLabel(item.type)} · ${item.key}`,
        showCancelButton: true,
        confirmButtonText: 'Delete',
      });
      if (!isConfirmed) return;
      try {
        await this.$gameApi.delete(`/analytics-configs/${item.id}`);
        await this.load();
        this.$swal.fire({
          icon: 'success',
          title: 'Deleted',
          timer: 1200,
          showConfirmButton: false,
        });
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: apiErrorMessage(e, 'Delete failed'),
          timer: 3000,
          showConfirmButton: false,
        });
      }
    },
  },
};
</script>

<style scoped>
.analytics-domains-expanded ul {
  padding-left: 1.25rem;
}

.analytics-configs-actions {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 4px 4px;
  white-space: nowrap;
}
</style>
